/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ObservableModel, ObserveModes } from '@typescript-kit/core';
import { Balance, BalanceData, BalanceModel } from './balance.model';

export class CarryoverFieldKey {
  static readonly FLEXTIME_BALANCE = 'flextimeBalance';
  static readonly HOLIDAY_BALANCE = 'holidayBalance';
  static readonly WEEK_HOURS = 'weekHours';
  static readonly WORK_PROPOSAL = 'workProposal';
  static readonly HOLIDAY_PROPOSAL = 'holidayProposal';
}

export interface CarryoverData {
  flextimeBalance: BalanceData;
  holidayBalance: BalanceData;
  weekHours?: number;
  workProposal?: number;
  holidayProposal?: number;
}

export class Carryover implements CarryoverData {
  public readonly flextimeBalance: Balance;
  public readonly holidayBalance: Balance;
  public readonly weekHours: number;
  public readonly work: number;
  public readonly workProposal: number;
  public readonly holiday: number;
  public readonly holidayProposal: number;

  constructor(data: CarryoverData) {
    this.flextimeBalance = (!data.flextimeBalance || data.flextimeBalance instanceof Balance)
      ? data.flextimeBalance as Balance
      : new Balance(data.flextimeBalance);
    this.holidayBalance = (!data.holidayBalance || data.holidayBalance instanceof Balance)
      ? data.holidayBalance as Balance
      : new Balance(data.holidayBalance);
    this.weekHours = data.weekHours;
    this.workProposal = data.workProposal;
    this.holidayProposal = data.holidayProposal;
  }
}

export class CarryoverModel extends ObservableModel implements CarryoverData {
  static readonly KEYS = Object.keys(CarryoverFieldKey).map((key) => CarryoverFieldKey[key]);

  constructor(data: CarryoverData) {
    super(data, CarryoverModel.KEYS, { observeMode: ObserveModes.FLAT });
  }

  get flextimeBalance(): BalanceModel {
    return this.get(CarryoverFieldKey.FLEXTIME_BALANCE);
  }

  get holidayBalance(): BalanceModel {
    return this.get(CarryoverFieldKey.HOLIDAY_BALANCE);
  }

  get weekHours(): number {
    return this.get(CarryoverFieldKey.WEEK_HOURS);
  }

  set weekHours(value: number) {
    this.set(CarryoverFieldKey.WEEK_HOURS, value);
  }

  get workProposal(): number {
    return this.get(CarryoverFieldKey.WORK_PROPOSAL);
  }

  get holidayProposal(): number {
    return this.get(CarryoverFieldKey.HOLIDAY_PROPOSAL);
  }

  protected setupValues(data: CarryoverData, exclusive: boolean): CarryoverData {
    this.setupBalance(CarryoverFieldKey.FLEXTIME_BALANCE, data, exclusive);
    this.setupBalance(CarryoverFieldKey.HOLIDAY_BALANCE, data, exclusive);
    return super.setupValues(data, exclusive) as CarryoverData;
  }

  protected setupBalance(key: string, data: CarryoverData, exclusive: boolean) {
    if ((exclusive || key in data) && !(data[key] instanceof BalanceModel)) {
      const originalValue = this.get(key) as BalanceModel;
      if (originalValue) {
        originalValue.setValues(data[key], { exclusive: true });
        data[key] = originalValue;
      } else {
        data[key] = new BalanceModel(data[key]);
      }
    }
  }
}
